import React, { useState, useRef } from 'react';
import Swal from 'sweetalert2';
import './OCRScanner.css';
import ZoneCropper from './ZoneCropper';
import expensePresets from '../config/expensePresets';

const OCRScanner = ({ onDataExtracted, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedExpenseType, setSelectedExpenseType] = useState('other');
  const [showInterface, setShowInterface] = useState(true);
  const [showDateCropper, setShowDateCropper] = useState(false);
  const [showAmountCropper, setShowAmountCropper] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [ocrResults, setOcrResults] = useState(null);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const expenseTypes = [
    { value: 'restaurant', label: 'Restaurant' },
    { value: 'hotel', label: 'Hôtel' },
    { value: 'carburant', label: 'Carburant' },
    { value: 'taxi', label: 'Taxi' },
    { value: 'carRental', label: 'Location de véhicule' },
    { value: 'plane', label: 'Avion' },
    { value: 'other', label: 'Autre' }
  ];

  const createProcessedImageFile = (blob, originalFile, fileNameFallback) => {
    const baseName = (originalFile?.name || fileNameFallback || 'ocr-scan')
      .replace(/\.[^.]+$/, '')
      .replace(/[^a-zA-Z0-9-_]/g, '_');

    return new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  };

  // Fonction pour redimensionner une image avant l'upload en conservant plus de détails
  const resizeImage = (file, options = {}) => {
    const {
      maxWidth = 1800,
      maxHeight = 1800,
      quality = 0.92,
      sharpen = true
    } = options;

    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      const sourceUrl = URL.createObjectURL(file);

      img.onload = () => {
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          if (sharpen) {
            ctx.filter = 'contrast(1.08) saturate(1.02)';
          }

          ctx.drawImage(img, 0, 0, width, height);
          ctx.filter = 'none';
        }

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(sourceUrl);
          resolve(blob || file);
        }, 'image/jpeg', quality);
      };

      img.onerror = () => {
        URL.revokeObjectURL(sourceUrl);
        resolve(file);
      };

      img.src = sourceUrl;
    });
  };

  const processOCR = async (file, cropType = null) => {
    setIsProcessing(true);
    if (!cropType) {
      setShowInterface(false);
      const tempImageUrl = URL.createObjectURL(file);
      setCurrentImageUrl(tempImageUrl);
    }

    try {
      Swal.fire({
        title: cropType === 'date' ? 'Traitement de la zone de date...' : 
               cropType === 'amount' ? 'Traitement de la zone du montant...' : 
               'Analyse en cours...',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const formData = new FormData();
      formData.append('image', file);
      formData.append('expenseType', selectedExpenseType);
      if (cropType === 'date') {
        formData.append('dateOnly', 'true'); // Indiquer qu'on traite seulement la date
      } else if (cropType === 'amount') {
        formData.append('amountOnly', 'true'); // Indiquer qu'on traite seulement le montant
      }

      const response = await fetch('/api/ocr/process-ocr', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();
      Swal.close();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors du traitement');
      }

      if (result.success && result.data) {
        if (cropType === 'date') {
          if (ocrResults) {
            const updatedResults = { ...ocrResults, date: result.data.date, dateConfidence: result.data.dateConfidence };
            setOcrResults(updatedResults);
            await showOCRResults(updatedResults);
          }
        } else if (cropType === 'amount') {
          if (ocrResults) {
            const updatedResults = { ...ocrResults, amount: result.data.amount, amountConfidence: result.data.amountConfidence };
            setOcrResults(updatedResults);
            await showOCRResults(updatedResults);
          }
        } else {
          setOcrResults(result.data);
          setShowInterface(false);
          if (result.data.justificatif) {
            const justificatifUrl = `/${result.data.justificatif}`;
            setCurrentImageUrl(justificatifUrl);
          }
          await showOCRResults(result.data);
        }
      } else {
        throw new Error('Aucune donnée extraite');
      }

    } catch (error) {
      console.error('Erreur OCR:', error);
      if (!cropType) {
        setShowInterface(true);
      }
      Swal.fire({
        icon: 'error',
        title: 'Erreur OCR',
        html: `
          <div style="text-align: left;">
            <p><strong>Erreur:</strong> ${error.message}</p>
            <br>
            <p><strong>Conseils pour améliorer la reconnaissance :</strong></p>
            <ul style="font-size: 0.9em; color: #666;">
              <li>Assurer un bon éclairage</li>
              <li>Tenir l'appareil stable</li>
              <li>Cadrer uniquement le ticket</li>
              <li>Éviter les reflets et ombres</li>
              <li>Vérifier que le texte est net</li>
            </ul>
          </div>
        `,
        confirmButtonText: 'Réessayer',
        width: '500px'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDateCropComplete = async (croppedImageBlob) => {
    setShowDateCropper(false);
    const resizedBlob = await resizeImage(croppedImageBlob, {
      maxWidth: 2200,
      maxHeight: 2200,
      quality: 0.95
    });
    const croppedFile = createProcessedImageFile(resizedBlob, null, 'date_crop');
    await processOCR(croppedFile, 'date');
  };

  const handleAmountCropComplete = async (croppedImageBlob) => {
    setShowAmountCropper(false);
    const resizedBlob = await resizeImage(croppedImageBlob, {
      maxWidth: 2200,
      maxHeight: 2200,
      quality: 0.95
    });
    const croppedFile = createProcessedImageFile(resizedBlob, null, 'amount_crop');
    await processOCR(croppedFile, 'amount');
  };

  const openDateCropper = (urlOverride = null) => {
    const imageUrl = urlOverride || currentImageUrl;
    
    if (imageUrl) {
      // S'assurer que l'URL est définie avant d'ouvrir
      if (urlOverride) {
        setCurrentImageUrl(urlOverride);
      }
      setShowDateCropper(true);
    } else {
    }
  };

  const openAmountCropper = (urlOverride = null) => {
    const imageUrl = urlOverride || currentImageUrl;
    
    if (imageUrl) {
      // S'assurer que l'URL est définie avant d'ouvrir
      if (urlOverride) {
        setCurrentImageUrl(urlOverride);
      }
      setShowAmountCropper(true);
    } else {
    }
  };

  const showOCRResults = async (data) => {
    // Déterminer l'URL de l'image à utiliser pour le recadrage
    const imageUrlForCrop = data.justificatif ? `/${data.justificatif}` : currentImageUrl;
    
    // Variable locale pour tracker l'ouverture du cropper
    let willOpenCropper = false;

    const parseAmountValue = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const detectedTvaRate = parseAmountValue(
      data.tva || (expensePresets[data.expense_type]?.defaultValues?.tva || 20)
    );

    const montantHT = data.montantHT ?? data.amount ?? '';
    const montantTTC = data.montantTTC ?? (
      montantHT !== '' ? parseAmountValue(montantHT) * (1 + detectedTvaRate / 100) : ''
    );
    
    const formatAmount = (amount) => {
      return amount ? parseFloat(amount).toFixed(2) : 'Non détecté';
    };

    const formatDate = (date) => {
      if (!date) return 'Non détectée';
      
      // Si c'est déjà au format DD/MM/YYYY, le parser correctement
      if (typeof date === 'string' && date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = date.split('/');
        // Créer une date au format MM/DD/YYYY pour JavaScript
        const jsDate = new Date(`${month}/${day}/${year}`);
        if (!isNaN(jsDate.getTime())) {
          return jsDate.toLocaleDateString('fr-FR');
        }
      }
      
      // Fallback : essayer de parser avec new Date()
      try {
        const d = new Date(date);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('fr-FR');
        }
      } catch {
        // Ignorer l'erreur
      }
      
      // Si rien ne marche, retourner la date brute
      return date;
    };

    const getExpenseTypeLabel = (type) => {
      const types = {
        'hotel': 'Hôtel',
        'restaurant': 'Restaurant',
        'carburant': 'Carburant',
        'taxi': 'Taxi',
        'carRental': 'Location de véhicule',
        'plane': 'Avion',
        'other': 'Autre'
      };
      return types[type] || 'Autre';
    };

    // Calculer un score de confiance global amélioré
    const globalConfidence = data.globalConfidence || 0;
    // Scores de confiance détaillés
    const confidenceScores = {
      amount: data.amountConfidence || (data.amount ? 60 : 0),
      date: data.dateConfidence || (data.date ? 60 : 0),
      type: data.typeConfidence || (data.expense_type !== 'other' ? 60 : 20),
      tva: data.tvaConfidence || (data.tva ? 60 : 0),
      description: data.descriptionConfidence || (data.description ? 60 : 0)
    };

    const { value: confirmed } = await Swal.fire({
      title: `Résultats OCR`,
      html: `         
          <!-- Données extraites -->
          <div style="background: white; border: 2px solid #e9ecef; padding: 20px; border-radius: 12px; margin-bottom: 20px;">
            <h4 style="margin: 0 0 15px 0; color: #495057;">Données extraites</h4>
            
            <div style="display: grid; gap: 12px;">
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
                <span><strong>Type:</strong></span>
                <span style="color: ${confidenceScores.type >= 60 ? '#28a745' : confidenceScores.type >= 40 ? '#ffc107' : '#dc3545'}; font-weight: 600;">
                  ${getExpenseTypeLabel(data.expense_type)}
                </span>
              </div>
              
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
                <span><strong>Montant (TTC):</strong></span>
                <div style="display: flex; align-items: center; gap: 10px;">
                  <span style="color: ${data.amount ? (confidenceScores.amount >= 60 ? '#28a745' : '#ffc107') : '#dc3545'}; font-weight: 600; font-size: 1.1em;">
                    ${formatAmount(montantTTC)} €
                  </span>
                  <button id="crop-amount-btn" style="
                    background: #007bff; 
                    color: white; 
                    border: none; 
                    padding: 6px 8px; 
                    border-radius: 4px; 
                    font-size: 0.8em; 
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 32px;
                    height: 28px;
                  ">
                    <i class="fa-solid fa-expand" style="font-size: 1.3em;"></i>
                  </button>
                </div>
              </div>
              
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
                <span><strong>Date:</strong></span>
                <div style="display: flex; align-items: center; gap: 10px;">
                  <span style="color: ${data.date ? (confidenceScores.date >= 60 ? '#28a745' : '#ffc107') : '#dc3545'}; font-weight: 600;">
                    ${formatDate(data.date)}
                  </span>
                  <button id="crop-date-btn" style="
                    background: #007bff; 
                    color: white; 
                    border: none; 
                    padding: 6px 8px; 
                    border-radius: 4px; 
                    font-size: 0.8em; 
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 32px;
                    height: 28px;
                  ">
                    <i class="fa-solid fa-expand" style="font-size: 1.3em;"></i>
                  </button>
                </div>
              </div>
              
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
                <span><strong>TVA:</strong></span>
                <span style="color: ${data.tva ? '#28a745' : '#ffc107'}; font-weight: 600;">
                  ${detectedTvaRate}%
                </span>
              </div>
              
              <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
                <span><strong>Description:</strong></span>
                <span style="color: ${data.description ? (confidenceScores.description >= 60 ? '#28a745' : '#ffc107') : '#dc3545'}; font-weight: 600; max-width: 60%; text-align: right;">
                  ${data.description || (data.descriptionNote ? 'À saisir manuellement' : 'Non détectée')}
                </span>
              </div>
              ${data.descriptionNote ? `
                <div style="padding: 8px; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 6px; margin-top: 5px; font-size: 0.85em; color: #856404;">
                  ${data.descriptionNote}
                </div>
              ` : ''}
              
              ${data.expense_type === 'carburant' && data.fuel_liters ? `
                <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #e8f5e8; border-radius: 8px; border: 1px solid #28a745;">
                  <span><strong>Litres:</strong></span>
                  <span style="color: #28a745; font-weight: 600;">
                    ${data.fuel_liters}L
                  </span>
                </div>
                
                ${data.price_per_liter ? `
                  <div style="display: flex; justify-content: space-between; align-items: center; padding: 10px; background: #e8f5e8; border-radius: 8px; border: 1px solid #28a745;">
                    <span><strong>Prix/Litre:</strong></span>
                    <span style="color: #28a745; font-weight: 600;">
                      ${data.price_per_liter}€/L
                    </span>
                  </div>
                ` : ''}
              ` : ''}
            </div>
          </div>

          
        </div>
      `,
      confirmButtonText: 'Utiliser ces données',
      cancelButtonText: 'Reprendre une photo',
      showCancelButton: true,
      confirmButtonColor: globalConfidence >= 60 ? '#28a745' : '#ffc107',
      cancelButtonColor: '#6c757d',
      width: '600px',
      customClass: {
        popup: 'ocr-results-popup'
      },
      didOpen: () => {
        // Ajouter l'événement au bouton de recadrage de date
        const cropDateBtn = document.getElementById('crop-date-btn');
        if (cropDateBtn) {
          cropDateBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            // Marquer qu'on va ouvrir le cropper
            willOpenCropper = true;
            Swal.close();
            // Ouvrir directement l'interface de recadrage avec l'URL déterminée
            setTimeout(() => {
              openDateCropper(imageUrlForCrop);
            }, 100); // Petit délai pour que Swal se ferme complètement
          });
        }

        // Ajouter l'événement au bouton de recadrage de montant
        const cropAmountBtn = document.getElementById('crop-amount-btn');
        if (cropAmountBtn) {
          cropAmountBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            // Marquer qu'on va ouvrir le cropper
            willOpenCropper = true;
            Swal.close();
            // Ouvrir directement l'interface de recadrage avec l'URL déterminée
            setTimeout(() => {
              openAmountCropper(imageUrlForCrop);
            }, 100); // Petit délai pour que Swal se ferme complètement
          });
        }
      }
    });

    if (confirmed) {
      const extractedData = {
        amount: montantTTC || '',
        date: data.date || '',
        expense_type: data.expense_type || 'other',
        tva: detectedTvaRate,
        description: data.description || '',
        descriptionNote: data.descriptionNote || null,
        raw_text: data.raw_text || '',
        fuel_liters: data.fuel_liters || null,
        price_per_liter: data.price_per_liter || null,
        justificatif: data.justificatif || null,
        autoSavedAsJustificatif: data.autoSavedAsJustificatif || false,
        montantHT,
        montantTTC,
        tvaIncluded: true
      };
      
      if (onDataExtracted) {
        onDataExtracted(extractedData);
      }
      
      onClose();
      
      return extractedData;
    } else {
      // Vérifier si on ouvre le cropper ou si c'est une vraie annulation
      if (willOpenCropper) {
        return null;
      } else {
        // L'utilisateur veut vraiment reprendre une photo
        // Réafficher l'interface puis déclencher automatiquement l'appareil photo
        setShowInterface(true);
        setTimeout(() => {
          if (cameraInputRef.current) {
            cameraInputRef.current.click();
          }
        }, 100); // Petit délai pour permettre à SweetAlert de se fermer et l'interface de se réafficher
        
        return null;
      }
    }
  };

  const handleCameraCapture = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const resizedBlob = await resizeImage(file, {
        maxWidth: 1800,
        maxHeight: 1800,
        quality: 0.92
      });
      const resizedFile = createProcessedImageFile(resizedBlob, file, file.name);
      
      // Traiter l'image directement (ne pas fermer l'interface OCR)
      await processOCR(resizedFile);
    } catch (error) {
      console.error('Erreur lors du traitement de l\'image:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de traiter l\'image',
        confirmButtonText: 'OK'
      });
    }

    // Reset de l'input pour permettre de reprendre la même photo
    event.target.value = '';
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Veuillez sélectionner une image (JPG, PNG, etc.)',
        confirmButtonText: 'OK'
      });
      return;
    }

    try {
      const resizedBlob = await resizeImage(file, {
        maxWidth: 1800,
        maxHeight: 1800,
        quality: 0.92
      });
      const resizedFile = createProcessedImageFile(resizedBlob, file, file.name);
      
      // Traiter l'image directement (ne pas fermer l'interface OCR)
      await processOCR(resizedFile);
    } catch (error) {
      console.error('Erreur lors du traitement de l\'image:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Impossible de traiter l\'image',
        confirmButtonText: 'OK'
      });
    }

    event.target.value = '';
  };

  return (
    <>
      {showInterface && (
        <>
          <div className="ocr-overlay" onClick={onClose}></div>
          <div className="ocr-scanner">
        <div className="ocr-header">
          <h3>
            Scanner une dépense
          </h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="ocr-content">
          {/* Sélecteur de type de dépense */}
          <div className="form-group">
            <label>
              Type de dépense :
            </label>
            <select 
              value={selectedExpenseType} 
              onChange={(e) => setSelectedExpenseType(e.target.value)}
              className="form-control"
            >
              {expenseTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <small className="form-hint">
              Sélectionnez le type avant de scanner pour une meilleure précision
            </small>
          </div>

          <div className="ocr-actions">
            <button 
              className="btn-primary camera-btn" 
              onClick={() => cameraInputRef.current?.click()}
              disabled={isProcessing}
            >
              <i className="fa-solid fa-camera"></i>
              Prendre une photo
            </button>

            <button 
              className="btn-secondary file-btn" 
              onClick={() => fileInputRef.current?.click()}
              disabled={isProcessing}
            >
              <i className="fa-solid fa-upload"></i>
              Choisir un fichier
            </button>

            {/* Input caméra - ouvre directement l'appareil photo */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleCameraCapture}
              style={{ display: 'none' }}
            />

            {/* Input fichier normal */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          <div className="ocr-info">
            <div className="tips-section">
              <p><strong>Conseils pour de meilleurs résultats :</strong></p>
              <ul>
                <li>Cadrez uniquement le ticket/reçu</li>
                <li>Évitez les reflets et ombres</li>
                <li>Assurez-vous que le texte est net</li>
                <li>Bonne luminosité recommandée</li>
              </ul>

              <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', padding: '6px', background: '#e8f5e8', borderRadius: '4px'}}>
                <small style={{color: '#28a745', fontWeight: '500'}}>L'image sera ajoutée temporairement au justificatif jusqu'à validation de la note</small>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
      
      {showDateCropper && currentImageUrl && (
        <ZoneCropper 
          imageUrl={currentImageUrl}
          onCropComplete={handleDateCropComplete}
          onClose={() => setShowDateCropper(false)}
          zoneType="date"
        />
      )}

      {showAmountCropper && currentImageUrl && (
        <ZoneCropper 
          imageUrl={currentImageUrl}
          onCropComplete={handleAmountCropComplete}
          onClose={() => setShowAmountCropper(false)}
          zoneType="amount"
        />
      )}
      
      {/* Debug: état des variables */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed', 
          bottom: '10px', 
          right: '10px', 
          background: 'rgba(0,0,0,0.8)', 
          color: 'white', 
          padding: '5px', 
          fontSize: '12px',
          borderRadius: '4px',
          zIndex: 9999
        }}>
          showDateCropper: {showDateCropper.toString()}<br/>
          showAmountCropper: {showAmountCropper.toString()}<br/>
          currentImageUrl: {currentImageUrl ? 'SET' : 'NULL'}<br/>
          showInterface: {showInterface.toString()}
        </div>
      )}
    </>
  );
};

export default OCRScanner; 
